import uFuzzy from '@leeoniya/ufuzzy';

/**
 * Configuration for a FuzzySearcher instance.
 *
 * @example
 * const config: FuzzySearcherConfig<Participant, ParticipantID> = {
 *     getKey: item => item.uuid,
 *     getSearchTexts: item => [item.overlayText ?? item.displayName ?? ''],
 * };
 */
export interface FuzzySearcherConfig<TItem, TKey extends string> {
    /** Extract a unique key from an item. */
    getKey: (item: TItem) => TKey;
    /** Extract one or more searchable text strings from an item. A match on any text includes the item. */
    getSearchTexts: (item: TItem) => string[];
}

/**
 * Represents the results of a fuzzy search operation.
 *
 * The `FuzzySearchResult` is a `Map` where each key is a `TKey`, and the value is an object
 * containing:
 *   - `key`: The key of the matched item.
 *   - `matches`: An array of match objects, each describing a segment of interest with:
 *       - `start`: The starting index of the match.
 *       - `end`: The ending index of the match.
 *       - `score` (optional): A numerical score indicating the relevance or quality of the match.
 */
export type FuzzySearchResult<TKey extends string> = Map<
    TKey,
    {
        key: TKey;
        matches: {start: number; end: number; score?: number}[];
    }
>;

/**
 * Generic fuzzy searcher that can search any list of items by configurable text fields.
 *
 * @example
 * const searcher = new FuzzySearcher({
 *     getKey: (item) => item.id,
 *     getSearchTexts: (item) => [item.name, item.email],
 * });
 * const results = searcher.search(items, 'query');
 */
export class FuzzySearcher<TItem, TKey extends string> {
    private readonly config: FuzzySearcherConfig<TItem, TKey>;

    constructor(config: FuzzySearcherConfig<TItem, TKey>) {
        this.config = config;
    }

    /**
     * Searches for items in the given list based on the provided search query.
     * @param items The list of items to search.
     * @param searchQuery The search filter string.
     * @returns A map of matched item keys to their match details.
     */
    search(items: TItem[], searchQuery: string): FuzzySearchResult<TKey> {
        const result = new Map<
            TKey,
            {
                key: TKey;
                matches: {start: number; end: number; score?: number}[];
            }
        >();

        if (!searchQuery) {
            for (const item of items) {
                const key = this.config.getKey(item);
                result.set(key, {key, matches: []});
            }
            return result;
        }

        const uf = new uFuzzy({
            unicode: true,
            interSplit: "[^\\p{L}\\d']+",
            intraSplit: '\\p{Ll}\\p{Lu}',
            intraBound: '\\p{L}\\d|\\d\\p{L}|\\p{Ll}\\p{Lu}',
            intraChars: "[\\p{L}\\d']",
            intraContr: "'\\p{L}{1,2}\\b",
        });

        const latinizedSearchQuery = uFuzzy.latinize(searchQuery);

        // Run fuzzy search for each text field and merge results
        const textFieldCount =
            items.length > 0
                ? this.config.getSearchTexts(items[0] as TItem).length
                : 0;

        for (let fieldIndex = 0; fieldIndex < textFieldCount; fieldIndex++) {
            const texts = items.map(item =>
                uFuzzy.latinize(
                    this.config.getSearchTexts(item)[fieldIndex] ?? '',
                ),
            );

            const idxs = uf.filter(texts, latinizedSearchQuery);
            if (!idxs || idxs.length === 0) {
                continue;
            }

            const info = uf.info(idxs, texts, latinizedSearchQuery);
            const order = uf.sort(info, texts, latinizedSearchQuery);

            for (const orderIdx of order) {
                const infoIdx = info.idx[orderIdx];
                if (infoIdx === undefined) {
                    continue;
                }
                const item = items[infoIdx];
                if (!item) {
                    continue;
                }
                const key = this.config.getKey(item);
                if (result.has(key)) {
                    continue;
                }
                const ranges = info.ranges[orderIdx] || [];
                const matches: {start: number; end: number}[] = [];
                for (let i = 0; i + 1 < ranges.length; i += 2) {
                    const start = ranges[i];
                    const end = ranges[i + 1];
                    if (start !== undefined && end !== undefined) {
                        matches.push({start, end: end - 1});
                    }
                }
                result.set(key, {key, matches});
            }
        }

        return result;
    }
}
